import React, { useState } from "react";
import axios from "axios";

/* =========================================================
   FIELD COMPONENT — module-scoped to prevent remount on render
========================================================= */
const Field = ({ label, id, type = "text", value, onChange, placeholder, autoComplete, icon, focusedField, setFocusedField }) => (
  <div className="space-y-1.5">
    <label
      htmlFor={id}
      className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400"
    >
      {label}
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
          {icon}
        </span>
      )}
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocusedField(id)}
        onBlur={() => setFocusedField(null)}
        required
        className={`
          w-full rounded-xl border bg-white dark:bg-neutral-800 px-4 py-3 text-sm
          text-neutral-900 dark:text-neutral-100 placeholder-neutral-400
          transition-all duration-200 outline-none
          ${icon ? "pl-10" : ""}
          ${focusedField === id
            ? "border-green-500 ring-2 ring-green-500/20 shadow-sm"
            : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
          }
        `}
      />
    </div>
  </div>
);

/* =========================================================
   FORGOT PASSWORD VIEW
========================================================= */
export const ForgotPasswordView = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= PASSWORD STRENGTH ================= */
  const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    const checks = [
      pwd.length >= 8,
      /[0-9]/.test(pwd),
      /[!@#$%^&*]/.test(pwd),
      /[a-zA-Z]/.test(pwd),
      pwd.length >= 12,
    ];
    const score = checks.filter(Boolean).length;
    if (score <= 2) return { label: "Weak", color: "bg-red-400", width: "w-1/4" };
    if (score === 3) return { label: "Fair", color: "bg-yellow-400", width: "w-2/4" };
    if (score === 4) return { label: "Good", color: "bg-blue-400", width: "w-3/4" };
    return { label: "Strong", color: "bg-green-500", width: "w-full" };
  };

  const strength = getPasswordStrength(password);

  const validatePassword = (pwd) =>
    /^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-zA-Z]).{8,}$/.test(pwd);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setSuccessMessage("");
    setLoading(true);

    if (!email || !password || !confirmPassword) {
      setMessage("All fields are required");
      setLoading(false);
      return;
    }
    if (!validatePassword(password)) {
      setMessage("Password must contain number, symbol, and 8+ characters");
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const res = await axios.post("/api/forgot-password", {
        email,
        password,
        confirmPassword,
      });

      if (res.data.success) {
        setSuccessMessage("Password reset successful! Redirecting to login…");
        setTimeout(() => onBack?.(), 1500);
      }
    } catch (err) {
      setMessage(err.response?.data?.message || "Password reset failed");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 px-4 py-10">
      <div className="w-full max-w-md">

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-green-500 shadow-lg shadow-green-500/30 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white tracking-tight">
            Reset your password
          </h1>
          <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            Enter your email and choose a new password
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">

          {/* Error banner */}
          {message && (
            <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="text-sm text-red-600 dark:text-red-400">{message}</p>
            </div>
          )}

          {/* Success banner */}
          {successMessage && (
            <div className="mb-5 flex items-center gap-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 px-4 py-3">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-700 dark:text-green-400 font-medium">{successMessage}</p>
            </div>
          )}

          {!successMessage && (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Email */}
              <Field
                label="Email"
                id="fp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
                autoComplete="email"
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                icon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
              />

              {/* New Password */}
              <div className="space-y-1.5">
                <label
                  htmlFor="fp-password"
                  className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400"
                >
                  New Password
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  </span>
                  <input
                    id="fp-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 chars, number & symbol"
                    onFocus={() => setFocusedField("fp-password")}
                    onBlur={() => setFocusedField(null)}
                    required
                    className={`
                      w-full rounded-xl border bg-white dark:bg-neutral-800 pl-10 pr-11 py-3 text-sm
                      text-neutral-900 dark:text-neutral-100 placeholder-neutral-400
                      transition-all duration-200 outline-none
                      ${focusedField === "fp-password"
                        ? "border-green-500 ring-2 ring-green-500/20 shadow-sm"
                        : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
                      }
                    `}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Strength bar */}
                {password && strength && (
                  <div className="pt-1 space-y-1">
                    <div className="h-1 w-full rounded-full bg-neutral-100 dark:bg-neutral-700 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${strength.color} ${strength.width}`} />
                    </div>
                    <p className="text-xs text-neutral-400">
                      Strength:{" "}
                      <span className={`font-semibold ${
                        strength.label === "Weak" ? "text-red-500" :
                        strength.label === "Fair" ? "text-yellow-500" :
                        strength.label === "Good" ? "text-blue-500" :
                        "text-green-500"
                      }`}>{strength.label}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label
                  htmlFor="fp-confirm"
                  className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 dark:text-neutral-400"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <input
                    id="fp-confirm"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your new password"
                    onFocus={() => setFocusedField("fp-confirm")}
                    onBlur={() => setFocusedField(null)}
                    required
                    className={`
                      w-full rounded-xl border bg-white dark:bg-neutral-800 pl-10 pr-10 py-3 text-sm
                      text-neutral-900 dark:text-neutral-100 placeholder-neutral-400
                      transition-all duration-200 outline-none
                      ${confirmPassword && confirmPassword !== password
                        ? "border-red-400 ring-2 ring-red-400/20"
                        : confirmPassword && confirmPassword === password
                        ? "border-green-500 ring-2 ring-green-500/20"
                        : focusedField === "fp-confirm"
                        ? "border-green-500 ring-2 ring-green-500/20 shadow-sm"
                        : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
                      }
                    `}
                  />
                  {confirmPassword && (
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                      {confirmPassword === password ? (
                        <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </span>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className={`
                    w-full py-3 rounded-xl text-sm font-semibold text-white
                    transition-all duration-200 tracking-wide
                    ${loading
                      ? "bg-green-400 cursor-not-allowed opacity-70"
                      : "bg-green-500 hover:bg-green-600 active:scale-[0.98] shadow-md shadow-green-500/25 hover:shadow-lg hover:shadow-green-500/30"
                    }
                  `}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Resetting…
                    </span>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </div>

            </form>
          )}

          {/* Back to login */}
          {!successMessage && (
            <p className="mt-5 text-center text-sm text-neutral-500 dark:text-neutral-400">
              Remember your password?{" "}
              <button
                type="button"
                onClick={() => onBack?.()}
                className="text-green-600 dark:text-green-400 font-semibold hover:underline"
              >
                Back to login
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordView;